<template>
  <div class="markdown-editor">
    <!-- Tabs and Toolbar -->
    <div class="editor-header">
      <div class="editor-tabs">
        <button
          type="button"
          :class="['tab-btn', { active: activeTab === 'write' }]"
          @click="activeTab = 'write'"
        >
          <feather-icon
            icon="EditIcon"
            size="14"
            class="mr-50"
          />
          {{ $t('Write') }}
        </button>
        <button
          type="button"
          :class="['tab-btn', { active: activeTab === 'preview' }]"
          @click="activeTab = 'preview'"
        >
          <feather-icon
            icon="EyeIcon"
            size="14"
            class="mr-50"
          />
          {{ $t('Preview') }}
        </button>
      </div>

      <!-- Formatting Toolbar -->
      <div
        v-if="activeTab === 'write'"
        class="formatting-toolbar"
      >
        <button
          v-b-tooltip.hover
          :title="$t('Add heading')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('heading')"
        >
          <feather-icon
            icon="TypeIcon"
            size="14"
          />
        </button>
        <button
          v-b-tooltip.hover
          :title="$t('Bold')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('bold')"
        >
          <feather-icon
            icon="BoldIcon"
            size="14"
          />
        </button>
        <button
          v-b-tooltip.hover
          :title="$t('Italic')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('italic')"
        >
          <feather-icon
            icon="ItalicIcon"
            size="14"
          />
        </button>
        <div class="toolbar-divider" />
        <button
          v-b-tooltip.hover
          :title="$t('Insert code')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('code')"
        >
          <feather-icon
            icon="CodeIcon"
            size="14"
          />
        </button>
        <button
          v-b-tooltip.hover
          :title="$t('Add link')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('link')"
        >
          <feather-icon
            icon="LinkIcon"
            size="14"
          />
        </button>
        <div class="toolbar-divider" />
        <button
          v-b-tooltip.hover
          :title="$t('Add bulleted list')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('ul')"
        >
          <feather-icon
            icon="ListIcon"
            size="14"
          />
        </button>
        <button
          v-b-tooltip.hover
          :title="$t('Add numbered list')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('ol')"
        >
          <feather-icon
            icon="HashIcon"
            size="14"
          />
        </button>
        <button
          v-b-tooltip.hover
          :title="$t('Add task list')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('task')"
        >
          <feather-icon
            icon="CheckSquareIcon"
            size="14"
          />
        </button>
        <div class="toolbar-divider" />
        <button
          v-b-tooltip.hover
          :title="$t('Add quote')"
          type="button"
          class="toolbar-btn"
          @click="insertMarkdown('quote')"
        >
          <feather-icon
            icon="MessageSquareIcon"
            size="14"
          />
        </button>
        <div class="toolbar-divider" />
        <button
          v-b-tooltip.hover
          :title="$t('Add attachment or video (max 50MB)')"
          type="button"
          class="toolbar-btn"
          @click="triggerFileUpload"
        >
          <feather-icon
            icon="PaperclipIcon"
            size="14"
          />
        </button>
        <button
          id="video-url"
          v-b-tooltip.hover
          :title="$t('Add Video Link')"
          type="button"
          class="toolbar-btn"
        >
          <feather-icon
            icon="TvIcon"
            size="14"
          />
        </button>
        <validation-observer ref="VideoUrlForm">
          <validation-provider
            v-slot="{ errors }"
            name="Video URL"
            rules="required|link"
          >
            <b-popover
              triggers="hover"
              target="video-url"
              placement="bottom"
              custom-class="video-url-popover"
            >
              <div class="d-flex">
                <b-form-input
                  v-model="videoUrl"
                  type="text"
                  autofocus
                  :placeholder="$t('Enter video URL')"
                  @keyup.esc="$root.$emit('bv::hide::popover')"
                />
                <b-button
                  :disabled="!videoUrl"
                  size="sm"
                  class="ml-1"
                  variant="flat-success"
                  @click="insertMarkdown('video')"
                >
                  <feather-icon icon="CheckIcon" />
                </b-button>
              </div>
              <small class="d-block text-danger">{{ errors[0] }}</small>
              <small class="text-muted">*{{ $t('Supported platforms: YouTube, Vimeo and Loom') }}</small>
            </b-popover>
          </validation-provider>
        </validation-observer>
        <input
          ref="fileInput"
          type="file"
          multiple
          accept="image/*,video/*,application/pdf,text/*,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.zip,.rar"
          style="display: none"
          @change="handleFileSelect"
        >
      </div>
    </div>

    <!-- Editor Area -->
    <div class="editor-content">
      <!-- Write Tab -->
      <div
        v-show="activeTab === 'write'"
        class="write-area"
      >
        <div class="textarea-wrapper">
          <b-form-textarea
            :id="id"
            ref="textarea"
            :value="value"
            :placeholder="placeholder"
            :rows="rows"
            class="markdown-textarea"
            @input="handleInput"
            @paste="handlePaste"
            @keydown="handleKeydown"
          />
        </div>

        <!-- Mention Dropdown - Outside textarea wrapper to avoid clipping -->
        <div
          v-if="showMentionDropdown"
          class="mention-dropdown"
          :style="mentionDropdownStyle"
        >
          <div class="mention-list">
            <div
              v-for="(member, index) in filteredMembers"
              :key="member.uuid"
              :class="['mention-item', { active: index === selectedMemberIndex }]"
              @mousedown.prevent="selectMention(member)"
              @mouseenter="selectedMemberIndex = index"
            >
              <b-avatar
                :src="getStorage(member.avatar)"
                :text="getInitials(member.name)"
                size="24"
                :variant="'light-primary'"
                class="mr-1"
              />
              <div class="mention-member-info">
                <div class="mention-member-name">
                  {{ member.name }}
                </div>
                <div class="mention-member-email">
                  {{ member.email }}
                </div>
              </div>
            </div>
            <div
              v-if="filteredMembers.length === 0"
              class="mention-item disabled"
            >
              {{ $t('No members found') }}
            </div>
          </div>
        </div>
        <div class="editor-footer">
          <small class="text-muted">
            <feather-icon
              icon="InfoIcon"
              size="12"
              class="mr-25"
            />
            {{ $t('Use Markdown to format your comment') }}
          </small>
        </div>
      </div>

      <!-- Preview Tab -->
      <div
        v-show="activeTab === 'preview'"
        class="preview-area"
        @click="onClick"
      >
        <div class="markdown-body">
          <markdown-it-vue
            :content="processMentionContent(value, mentions.length ? mentions : boardMembers)"
            :options="options"
          />
        </div>
      </div>
    </div>

    <CoolLightBox
      :items="fetchImages || []"
      :index="coolLightBoxIndex"
      @close="coolLightBoxIndex = null"
    />
  </div>
</template>

<script>
import {
  BFormTextarea, BPopover, BFormInput, BButton, BAvatar, VBTooltip,
} from 'bootstrap-vue'
import boardsApi from '@/plugins/graphql/boards'
import { mapActions } from 'vuex'
import MarkdownItVue from 'markdown-it-vue'
import { mixin as clickaway } from 'vue-clickaway'
import { ValidationObserver, ValidationProvider, extend } from 'vee-validate'
import CoolLightBox from 'vue-cool-lightbox'
import { processMentionContent, getVideoEmbed, fetchImagesFromMarkdown } from './markdown'
import 'prismjs/themes/prism-tomorrow.css'

extend('link', value => {
  const pattern = /^(https?:\/\/)?([\w-])+\.{1}([a-zA-Z]{2,63})([/\w\-.?=&%+]*)*\/?$/
  if (pattern.test(value)) {
    return true
  }
  return 'This field must be a valid URL'
})

export default {
  name: 'MarkdownEditor',
  components: {
    BFormTextarea,
    BPopover,
    BFormInput,
    BButton,
    BAvatar,
    MarkdownItVue,
    CoolLightBox,
    ValidationObserver,
    ValidationProvider,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  mixins: [clickaway],
  props: {
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: 'Add description...',
    },
    rows: {
      type: [String, Number],
      default: 8,
    },
    id: {
      type: String,
      default: 'markdown-editor',
    },
    taskUid: {
      type: String,
      default: null,
    },
    boardMembers: {
      type: Array,
      default: () => [],
    },
    mentions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      processMentionContent,
      activeTab: 'write',
      uploadingFiles: new Set(),
      fileUploadQueue: [],
      videoUrl: '',
      options: {
        markdownIt: {
          linkify: true,
          html: true,
        },
        linkAttributes: {
          attrs: {
            target: '_blank',
            rel: 'noopener',
          },
        },
        image: {
          viewer: false,
        },
      },
      coolLightBoxIndex: null,
      randomToken: Math.random().toString(36).substring(2, 15),
      showMentionDropdown: false,
      mentionSearchQuery: '',
      mentionStartPos: -1,
      selectedMemberIndex: 0,
      mentionDropdownStyle: {},
    }
  },
  computed: {
    filteredMembers() {
      if (!this.mentionSearchQuery) return this.boardMembers
      const query = this.mentionSearchQuery.toLowerCase()
      return this.boardMembers.filter(member => member.name?.toLowerCase().includes(query)
        || member.email?.toLowerCase().includes(query))
    },
    fetchImages() {
      return fetchImagesFromMarkdown(this.value) || []
    },
  },
  watch: {
    activeTab(val) {
      if (val === 'preview') {
        this.highlightCode()
      }
    },
    value() {
      if (this.activeTab === 'preview') {
        this.highlightCode()
      }
    },
  },
  methods: {
    ...mapActions('boards', ['setTemporaryAssetToken']),
    getInitials(name) {
      if (!name) return '?'
      return name
        .split(' ')
        .map(word => word[0])
        .join('')
        .toUpperCase()
        .substring(0, 2)
    },
    handleInput(value) {
      this.$emit('input', value)
      this.checkForMentionTrigger()
    },
    handleKeydown(e) {
      if (this.showMentionDropdown) {
        switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          this.selectedMemberIndex = Math.min(
            this.selectedMemberIndex + 1,
            this.filteredMembers.length - 1,
          )
          break
        case 'ArrowUp':
          e.preventDefault()
          this.selectedMemberIndex = Math.max(this.selectedMemberIndex - 1, 0)
          break
        case 'Enter':
        case 'Tab':
          e.preventDefault()
          if (this.filteredMembers[this.selectedMemberIndex]) {
            this.selectMention(this.filteredMembers[this.selectedMemberIndex])
          }
          break
        case 'Escape':
          e.preventDefault()
          this.closeMentionDropdown()
          break
        default:
          break
        }
      }
    },
    checkForMentionTrigger() {
      this.$nextTick(() => {
        const textarea = this.$refs.textarea?.$refs?.input || this.$refs.textarea?.$el
        if (!textarea) return

        const cursorPos = textarea.selectionStart
        const textBeforeCursor = this.value.substring(0, cursorPos)

        // Find the last @ symbol before cursor
        const lastAtPos = textBeforeCursor.lastIndexOf('@')

        if (lastAtPos === -1) {
          this.closeMentionDropdown()
          return
        }

        // Check if there's a space or newline between @ and cursor
        const textAfterAt = textBeforeCursor.substring(lastAtPos + 1)
        if (textAfterAt.includes(' ') || textAfterAt.includes('\n')) {
          this.closeMentionDropdown()
          return
        }

        // Show mention dropdown
        this.mentionStartPos = lastAtPos
        this.mentionSearchQuery = textAfterAt
        this.selectedMemberIndex = 0
        this.showMentionDropdown = true
        this.positionMentionDropdown()
      })
    },
    positionMentionDropdown() {
      const textarea = this.$refs.textarea?.$refs?.input || this.$refs.textarea?.$el
      if (!textarea) return

      const textareaRect = textarea.getBoundingClientRect()
      const cursorPosition = this.getCursorCoordinates(textarea, textarea.selectionStart)

      // Calculate fixed position relative to viewport
      const top = textareaRect.top + cursorPosition.top + cursorPosition.height + 4
      const left = textareaRect.left + cursorPosition.left

      // Ensure dropdown doesn't go off-screen
      const maxTop = window.innerHeight - 250 // 250px is approximate dropdown height
      const maxLeft = window.innerWidth - 320 // 320px is dropdown width

      this.mentionDropdownStyle = {
        top: `${Math.min(top, maxTop)}px`,
        left: `${Math.min(left, maxLeft)}px`,
      }
    },
    getCursorCoordinates(element, position) {
      const div = document.createElement('div')
      const style = window.getComputedStyle(element)

      // Copy textarea styles to div
      const properties = [
        'boxSizing', 'width', 'height', 'overflowX', 'overflowY',
        'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
        'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
        'fontFamily', 'fontSize', 'fontWeight', 'lineHeight',
        'textAlign', 'textTransform', 'wordSpacing', 'letterSpacing',
      ]

      properties.forEach(prop => {
        div.style[prop] = style[prop]
      })

      div.style.position = 'absolute'
      div.style.visibility = 'hidden'
      div.style.whiteSpace = 'pre-wrap'
      div.style.wordWrap = 'break-word'
      div.style.top = '0'
      div.style.left = '0'

      document.body.appendChild(div)

      // Add text up to cursor position
      const textBeforeCursor = element.value.substring(0, position)
      div.textContent = textBeforeCursor

      // Create a span for cursor position
      const span = document.createElement('span')
      span.textContent = element.value.substring(position, position + 1) || '.'
      div.appendChild(span)

      const coordinates = {
        top: span.offsetTop - element.scrollTop,
        left: span.offsetLeft - element.scrollLeft,
        height: span.offsetHeight,
      }

      document.body.removeChild(div)

      return coordinates
    },
    selectMention(member) {
      const textarea = this.$refs.textarea?.$refs?.input || this.$refs.textarea?.$el
      if (!textarea) return

      const beforeMention = this.value.substring(0, this.mentionStartPos)
      const afterCursor = this.value.substring(textarea.selectionStart)

      // Insert mention in format @Name
      const mentionText = `@${member.uuid} (${member.name})`
      const newValue = `${beforeMention + mentionText} ${afterCursor}`

      this.$emit('input', newValue)

      this.closeMentionDropdown()

      // Set cursor position after the mention
      this.$nextTick(() => {
        const newCursorPos = beforeMention.length + mentionText.length + 1
        textarea.focus()
        textarea.setSelectionRange(newCursorPos, newCursorPos)
      })
    },
    closeMentionDropdown() {
      this.showMentionDropdown = false
      this.mentionSearchQuery = ''
      this.mentionStartPos = -1
      this.selectedMemberIndex = 0
    },
    handlePaste(e) {
      const { items } = e.clipboardData

      Array.from(items).forEach(item => {
        if (item.type.indexOf('image') !== -1) {
          const file = item.getAsFile()
          this.uploadAttachment(file)

          e.preventDefault()
        }
      })
    },
    async insertMarkdown(type) {
      const textarea = this.$refs.textarea?.$refs?.input || this.$refs.textarea?.$el
      if (!textarea) {
        console.warn('Textarea ref not found')
        return
      }

      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const selectedText = this.value.substring(start, end)
      const beforeText = this.value.substring(0, start)
      const afterText = this.value.substring(end)

      let newText = ''
      let cursorOffset = 0
      let attachmentMarkdown = ''
      let embed = ''

      switch (type) {
      case 'video':
        await this.$refs.VideoUrlForm.validate().then(valid => {
          if (valid) {
            this.$root.$emit('bv::hide::popover')
            embed = getVideoEmbed(this.videoUrl)
            attachmentMarkdown = ''

            if (embed) {
              attachmentMarkdown = `${!beforeText ? '' : '\n'}${embed}\n`
            }

            newText = `${beforeText}${attachmentMarkdown}${afterText}`
            this.videoUrl = ''
          }
        })
        break
      case 'heading':
        newText = `${beforeText}### ${selectedText || 'Heading'}${afterText}`
        cursorOffset = selectedText ? 0 : -'Heading'.length
        break
      case 'bold':
        newText = `${beforeText}**${selectedText || 'bold text'}**${afterText}`
        cursorOffset = selectedText ? 0 : -'bold text**'.length
        break
      case 'italic':
        newText = `${beforeText}_${selectedText || 'italic text'}_${afterText}`
        cursorOffset = selectedText ? 0 : -'italic text_'.length
        break
      case 'code':
        if (selectedText.includes('\n')) {
          newText = `${beforeText}\n\`\`\`\n${selectedText || 'code'}\n\`\`\`\n${afterText}`
          cursorOffset = selectedText ? 0 : -'\ncode\n```\n'.length
        } else {
          newText = `${beforeText}\`${selectedText || 'code'}\`${afterText}`
          cursorOffset = selectedText ? 0 : -'code`'.length
        }
        break
      case 'link':
        newText = `${beforeText}[${selectedText || 'text'}](url)${afterText}`
        cursorOffset = selectedText ? -4 : -'text](url)'.length
        break
      case 'ul':
        newText = `${beforeText}\n- ${selectedText || 'List item'}${afterText}`
        cursorOffset = selectedText ? 0 : -'List item'.length
        break
      case 'ol':
        newText = `${beforeText}\n1. ${selectedText || 'List item'}${afterText}`
        cursorOffset = selectedText ? 0 : -'List item'.length
        break
      case 'task':
        newText = `${beforeText}\n- [ ] ${selectedText || 'Task'}${afterText}`
        cursorOffset = selectedText ? 0 : -'Task'.length
        break
      case 'quote':
        newText = `${beforeText}\n> ${selectedText || 'Quote'}${afterText}`
        cursorOffset = selectedText ? 0 : -'Quote'.length
        break
      default:
        return
      }

      if (newText) {
        this.$emit('input', newText)

        this.$nextTick(() => {
          const newPosition = start + newText.length - beforeText.length - afterText.length + cursorOffset
          textarea.focus()
          textarea.setSelectionRange(newPosition, newPosition)
          this.$refs.VideoUrlForm.reset()
        })
      }
    },
    highlightCode() {
      this.$nextTick(() => {
        if (window.Prism && this.activeTab === 'preview') {
          window.Prism.highlightAll()
        }
      })
    },
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },
    async handleFileSelect(event) {
      const { files } = event.target
      if (!files || files.length === 0) return

      const MAX_VIDEO_SIZE = 50 * 1024 * 1024 // 50MB

      for (let i = 0; i < files.length; i += 1) {
        const file = files[i]
        if (file.type.startsWith('video/') && file.size > MAX_VIDEO_SIZE) {
          this.showError('Video exceeds the 50MB video size limit.')
        } else {
          this.uploadAttachment(file)
        }
      }

      // Reset the input
      this.$refs.fileInput.value = ''
    },
    async uploadAttachment(file) {
      const fileId = `${file.name}-${Date.now()}`
      this.uploadingFiles.add(fileId)
      this.$emit('uploading', true)

      try {
        // Convert file to base64
        const base64String = await this.fileToBase64(file)

        // Emit a placeholder in the editor while uploading
        const beforeText = this.value
        const attachmentPlaceholder = `\n[Uploading: ${file.name}...]\n`
        this.$emit('input', beforeText + attachmentPlaceholder)

        // Upload the file
        const uploadResponse = await this.uploadFile({
          taskUid: this.taskUid,
          base64Data: base64String,
          fileName: file.name,
          mimeType: file.type,
        })

        let attachmentMarkdown = ''
        const fileType = file.type

        if (fileType.startsWith('image/')) {
          // Standard markdown image
          attachmentMarkdown = `\n![Image](${uploadResponse.url})\n`
        } else if (fileType.startsWith('video/')) {
          // HTML5 video element (html: true is enabled in markdownIt options)
          attachmentMarkdown = `\n<video controls style="max-width:100%;border-radius:4px"><source src="${uploadResponse.url}" type="${fileType}">Your browser does not support the video tag.</video>\n`
        } else {
          // Generic file link
          attachmentMarkdown = `\n[Download ${file.name}](${uploadResponse.url})\n`
        }

        const updatedText = this.value.replace(
          `[Uploading: ${file.name}...]`,
          attachmentMarkdown,
        )

        this.$emit('input', updatedText)

        // Emit upload success event
        this.$emit('file-uploaded', {
          file,
          url: uploadResponse.url,
          name: file.name,
        })
      } catch (error) {
        console.error('File upload failed:', error)

        // Remove placeholder on error
        const errorText = this.value.replace(
          `[Uploading: ${file.name}...]`,
          '',
        )
        this.$emit('input', errorText)

        this.showError(`Failed to upload ${file.name}: ${error.message}`)

        this.$emit('file-upload-error', {
          file,
          error: error.message,
        })
      } finally {
        this.uploadingFiles.delete(fileId)
        this.$emit('uploading', false)
      }
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()

        reader.onload = () => {
          resolve(reader.result)
        }

        reader.onerror = error => {
          reject(error)
        }

        reader.readAsDataURL(file)
      })
    },
    async uploadFile(data) {
      const payload = data
      if (!payload.taskUid) {
        payload.taskUid = null
        payload.temporaryToken = this.randomToken
        this.setTemporaryAssetToken(this.randomToken)
      }
      try {
        const response = await boardsApi.uploadTaskAsset({ input: payload })
        return response.data.uploadTaskAsset.data
      } catch (error) {
        throw new Error(error.response?.data?.message || error.message || 'Upload failed')
      }
    },
    onClick({ target }) {
      if (target.tagName === 'IMG') {
        const src = target.getAttribute('src')
        const index = this.fetchImages.findIndex(img => img === src)

        if (index !== -1) {
          this.coolLightBoxIndex = index
        }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/base/pages/kanban-side-panel.scss";

// Upload indicator styles
.uploading-indicator {
  padding: 0.5rem 1rem;
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  color: #92400e;
  font-size: 0.875rem;
  margin-top: 0.5rem;
}

// Mention dropdown styles
.write-area,
.textarea-wrapper {
  position: relative;
}

.mention-dropdown {
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 0.375rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 9999;
  max-height: 240px;
  overflow-y: auto;
  min-width: 280px;
  max-width: 320px;
}

.mention-list {
  padding: 0.25rem;
}

.mention-item {
  display: flex;
  align-items: center;
  padding: 0.5rem;
  cursor: pointer;
  border-radius: 0.25rem;
  transition: background-color 0.15s;

  &:hover,
  &.active {
    background-color: #f1f5f9;
  }

  &.disabled {
    cursor: default;
    color: #94a3b8;

    &:hover {
      background-color: transparent;
    }
  }
}

.mention-member-info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.mention-member-name {
  font-weight: 500;
  font-size: 0.875rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mention-member-email {
  font-size: 0.75rem;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

// Dark mode styles
.dark-layout {
  .mention-dropdown {
    background: #283046;
    border-color: #404656;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2);
  }

  .mention-item {
    color: #d0d2d6;

    &:hover,
    &.active {
      background-color: #3b4253;
    }

    &.disabled {
      color: #676d7d;

      &:hover {
        background-color: transparent;
      }
    }
  }

  .mention-member-name {
    color: #d0d2d6;
  }

  .mention-member-email {
    color: #676d7d;
  }
}
</style>

<style>
.video-url-popover .popover-body {
  width: 420px !important;
}

/* Mention display in preview */
.markdown-body .mention {
  background-color: #dbeafe;
  color: #1e40af;
  padding: 0.125rem 0.375rem;
  border-radius: 0.25rem;
  font-weight: 500;
  transition: background-color 0.15s;
}

.markdown-body .mention:hover {
  background-color: #bfdbfe;
}

/* Dark mode mention styles */
.dark-layout .markdown-body .mention {
  background-color: #1e3a8a;
  color: #93c5fd;
}

.dark-layout .markdown-body .mention:hover {
  background-color: #1e40af;
}
</style>
